import { useState } from 'react';
import { useCompany } from '../App';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Button, Card, DataTable, ErrorBanner, Field, Flash, Input, Modal, Select, Spinner } from '../components/ui';
import { date, localToday, pricePerShare, valuationKind } from '../lib/format';

const KINDS = ['FMV_409A', 'BOARD', 'POST_MONEY'];

/**
 * The valuation history a strike price or a 409A compliance check is judged
 * against. The API has always taken these (GET/POST /valuations, gated
 * VIEWER/LEGAL); nothing in the UI called either route until this page did —
 * the only way to record one was a direct API call, which is exactly what
 * "legal can onboard employees" is supposed to make unnecessary.
 */
export default function Valuations() {
  const { companyId, role } = useCompany();
  const [recording, setRecording] = useState(false);
  const [editing, setEditing] = useState(null);
  const [flash, setFlash] = useState(null);
  const list = useAsync(() => api.valuations(companyId), [companyId]);
  const canRecord = ['LEGAL', 'ADMIN', 'OWNER'].includes(role);

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Valuations</h1>
          <p>
            409A fair market value, board-determined and post-money valuations, newest first.
            An option's strike price is checked against whichever 409A was in effect on its
            grant date — see Data Warnings on the cap table export.
          </p>
        </div>
        {canRecord ? (
          <div className="page-actions">
            <Button variant="primary" onClick={() => setRecording(true)}>Record valuation</Button>
          </div>
        ) : null}
      </div>

      <Flash message={flash} onDone={() => setFlash(null)} />

      {list.loading ? <Spinner /> : list.error ? <ErrorBanner error={list.error} onRetry={list.reload} /> : (
        <Card padded={false}>
          <DataTable
            rowKey={(r) => r.id}
            empty="No valuations recorded yet."
            columns={[
              { key: 'kind', header: 'Kind', render: (r) => valuationKind(r.kind) },
              { key: 'price', header: 'Price/share', align: 'right', numeric: true, render: (r) => pricePerShare(r.price_per_share) },
              { key: 'effective', header: 'Effective', render: (r) => date(r.effective_date) },
              { key: 'expires', header: 'Expires', render: (r) => (r.expires_on ? date(r.expires_on) : '—') },
              { key: 'provider', header: 'Provider', render: (r) => r.provider ?? '—' },
              { key: 'by', header: 'Recorded by', render: (r) => r.created_by_email ?? '—' },
              {
                key: 'actions',
                header: '',
                render: (r) => (canRecord ? (
                  <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>Edit</Button>
                ) : null),
              },
            ]}
            rows={list.data}
          />
        </Card>
      )}

      {recording ? (
        <RecordValuationModal
          companyId={companyId}
          onClose={() => setRecording(false)}
          onSaved={(msg) => { setRecording(false); setFlash(msg); list.reload(); }}
        />
      ) : null}
      {editing ? (
        <EditValuationModal
          companyId={companyId}
          valuation={editing}
          onClose={() => setEditing(null)}
          onSaved={(msg) => { setEditing(null); setFlash(msg); list.reload(); }}
        />
      ) : null}
    </div>
  );
}

function RecordValuationModal({ companyId, onClose, onSaved }) {
  const today = localToday();
  const [form, setForm] = useState({
    kind: 'FMV_409A', pricePerShare: '', effectiveDate: today, expiresOn: '', provider: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const fe = error?.fieldErrors ?? {};

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = {
        kind: form.kind,
        pricePerShare: form.pricePerShare,
        effectiveDate: form.effectiveDate,
      };
      if (form.expiresOn) body.expiresOn = form.expiresOn;
      if (form.provider) body.provider = form.provider;
      await api.createValuation(companyId, body);
      onSaved(`Recorded a ${valuationKind(form.kind)} valuation at ${pricePerShare(form.pricePerShare)}/share.`);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Record valuation" onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={saving} onClick={submit}>Save</Button></>}>
      <ErrorBanner error={error} />
      <form onSubmit={submit}>
        <div className="form-grid">
          <Field label="Kind" required>
            <Select value={form.kind} onChange={set('kind')}>
              {KINDS.map((k) => <option key={k} value={k}>{valuationKind(k)}</option>)}
            </Select>
          </Field>
          <Field label="Price per share" required error={fe.pricePerShare}>
            <Input value={form.pricePerShare} onChange={set('pricePerShare')} inputMode="decimal" required autoFocus />
          </Field>
          <Field label="Effective date" required error={fe.effectiveDate}>
            <Input type="date" value={form.effectiveDate} onChange={set('effectiveDate')} required />
          </Field>
          <Field label="Expires" hint="409A safe harbor is typically 12 months, or sooner on a material event">
            <Input type="date" value={form.expiresOn} onChange={set('expiresOn')} />
          </Field>
          <Field label="Provider" hint="e.g. the appraisal firm, or “Board resolution 2026-06-01”">
            <Input value={form.provider} onChange={set('provider')} />
          </Field>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Corrects a data-entry error -- unlike a vesting schedule or equity plan,
 * nothing else references a valuation by id, so this is safe to allow at
 * any time rather than needing an "unused" guard.
 */
function EditValuationModal({ companyId, valuation, onClose, onSaved }) {
  const [form, setForm] = useState({
    kind: valuation.kind,
    pricePerShare: valuation.price_per_share ?? '',
    // effective_date/expires_on arrive as full timestamps (see date()'s own
    // doc comment in lib/format.js) -- an <input type="date"> needs exactly
    // YYYY-MM-DD or it silently renders blank.
    effectiveDate: valuation.effective_date ? String(valuation.effective_date).slice(0, 10) : '',
    expiresOn: valuation.expires_on ? String(valuation.expires_on).slice(0, 10) : '',
    provider: valuation.provider ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const fe = error?.fieldErrors ?? {};

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = {
        kind: form.kind,
        price_per_share: form.pricePerShare,
        effective_date: form.effectiveDate,
        expires_on: form.expiresOn || null,
        provider: form.provider || null,
      };
      await api.updateValuation(companyId, valuation.id, body);
      onSaved(`Updated the ${valuationKind(form.kind)} valuation.`);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Edit valuation" onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={saving} onClick={submit}>Save</Button></>}>
      <ErrorBanner error={error} />
      <form onSubmit={submit}>
        <div className="form-grid">
          <Field label="Kind" required>
            <Select value={form.kind} onChange={set('kind')}>
              {KINDS.map((k) => <option key={k} value={k}>{valuationKind(k)}</option>)}
            </Select>
          </Field>
          <Field label="Price per share" required error={fe.price_per_share}>
            <Input value={form.pricePerShare} onChange={set('pricePerShare')} inputMode="decimal" required autoFocus />
          </Field>
          <Field label="Effective date" required error={fe.effective_date}>
            <Input type="date" value={form.effectiveDate} onChange={set('effectiveDate')} required />
          </Field>
          <Field label="Expires" hint="409A safe harbor is typically 12 months, or sooner on a material event">
            <Input type="date" value={form.expiresOn} onChange={set('expiresOn')} />
          </Field>
          <Field label="Provider" hint="e.g. the appraisal firm, or “Board resolution 2026-06-01”">
            <Input value={form.provider} onChange={set('provider')} />
          </Field>
        </div>
      </form>
    </Modal>
  );
}
